import * as flatten from 'lodash/flatten'
import { MoneyflowType } from 'interfaces/moneyflow'
import { AccountType } from 'interfaces/accounts'
import {
  MetaData,
  TypedMoneyflowCategory,
  DebtAssetCategoryId,
  CollectionCategory,
  Category,
  CategoryId,
  MoneyflowCategoryGroup,
  MoneyflowCategoryType,
} from 'interfaces/meta'
import { getItemFromCollection, getItemsFromCollection } from 'helpers/normalizeHelpers'
import { TOTAL_SPENDING_TRACKER_CATEGORY_NAME, TOTAL_INCOME_TRACKER_CATEGORY_NAME } from 'constants/moneyflow'
import { ALL_DEBTS_TRACKER_CATEGORY_NAME, ALL_ASSETS_TRACKER_CATEGORY_NAME } from 'constants/accounts'
import { ALL_CATEGORY_ID, TRANSFER_CATEGORY_ID } from 'constants/meta'
import { TIMEFRAME_TYPES } from 'constants/datetime'
import { Timeframe } from 'interfaces/chart'
import { MINIMUM_BAR_WIDTH } from 'constants/chart'
export const COLOR_SET = {
  member: [
    '#495ab9',
    '#7a86cc',
    '#aab2de',
    '#dbdef1',
    '#5565be',
    '#8691d0',
    '#b6bde3',
    '#e7e9f6',
    '#6170c2',
    '#929cd5',
    '#c2c8e8',
    '#3d4b9a',
    '#6d7bc7',
    '#9ea7da',
    '#ced3ec',
    '#7a86cc',
  ],
  peers: [
    '#f88484',
    '#faa5a5',
    '#fcc6c6',
    '#fee6e6',
    '#f88c8c',
    '#faadad',
    '#fccece',
    '#feefef',
    '#f99494',
    '#fbb5b5',
    '#fdd6d6',
    '#cf6e6e',
    '#f99d9d',
    '#fbbdbd',
    '#fddede',
    '#faa5a5',
  ],
  others: [
    '#b06078',
    '#c58a9c',
    '#dab5c0',
    '#efdfe4',
    '#b56b81',
    '#ca95a5',
    '#dfbfc9',
    '#f4eaed',
    '#bb758a',
    '#d0a0ae',
    '#e5cad2',
    '#935064',
    '#c08093',
    '#d5aab7',
    '#ead5db',
    '#c58a9c',
  ],
  memberText: [
    '#ffffff',
    '#ffffff',
    '#4351a2',
    '#4351a2',
    '#ffffff',
    '#ffffff',
    '#4351a2',
    '#4351a2',
    '#ffffff',
    '#4351a2',
    '#4351a2',
    '#ffffff',
    '#ffffff',
    '#ffffff',
    '#4351a2',
    '#ffffff',
  ],
  peersText: [
    '#ffffff',
    '#ffffff',
    '#dd1b07',
    '#dd1b07',
    '#ffffff',
    '#ffffff',
    '#dd1b07',
    '#dd1b07',
    '#ffffff',
    '#dd1b07',
    '#dd1b07',
    '#ffffff',
    '#ffffff',
    '#ffffff',
    '#dd1b07',
    '#ffffff',
  ],
  othersText: [
    '#ffffff',
    '#ffffff',
    '#69273a',
    '#69273a',
    '#ffffff',
    '#ffffff',
    '#69273a',
    '#69273a',
    '#ffffff',
    '#69273a',
    '#69273a',
    '#ffffff',
    '#ffffff',
    '#ffffff',
    '#69273a',
    '#ffffff',
  ],
}

export function getMetaCategoriesByType (type: MoneyflowType | AccountType, meta: MetaData): CollectionCategory {

  if (type === 'spending') {
    return meta.spending_categories
  }

  if (type === 'income') {
    return meta.income_categories
  }

  if (type === 'debts') {
    return meta.debt_categories
  }

  return meta.asset_categories
}

const assetsMap: Partial<Record<DebtAssetCategoryId, boolean>> = {
  savings: true,
  cd: true,
  investment: true,
  asset_other: true,
  asset_auto: true,
  home: true,
}
export const isAssetsCategory = (category: DebtAssetCategoryId) => {
  return !!assetsMap[category]
}

export function resolveCategory (categoryId: CategoryId | null, toBackend: true): CategoryId | null
export function resolveCategory (categoryId: CategoryId | null, toBackend: false): CategoryId
export function resolveCategory (categoryId: CategoryId | null, toBackend: boolean): CategoryId | null {
  if (toBackend && categoryId === ALL_CATEGORY_ID) {
    return null
  }
  if (!toBackend && categoryId === null) {
    return ALL_CATEGORY_ID
  }

  return categoryId
}

export function getTrackerCategoryName ({ id, value }: Category, type: MoneyflowType | AccountType): string {
  let categoryName = value
  if (id !== ALL_CATEGORY_ID) {
    return value
  }

  if (type === 'spending') {
    categoryName = TOTAL_SPENDING_TRACKER_CATEGORY_NAME
  }
  if (type === 'income') {
    categoryName = TOTAL_INCOME_TRACKER_CATEGORY_NAME
  }
  if (type === 'debts') {
    categoryName = ALL_DEBTS_TRACKER_CATEGORY_NAME
  }
  if (type === 'assets') {
    categoryName = ALL_ASSETS_TRACKER_CATEGORY_NAME
  }

  return categoryName
}

export function getFriendlyCategoryTypeName (category: TypedMoneyflowCategory): string {
  return category.type === null ? 'transfer' : category.type
}

export function getTimeframeCopy (timeframe: Timeframe): string {
  const timeframeObj = TIMEFRAME_TYPES.find(({ id }) => id === timeframe)
  return timeframeObj ? timeframeObj.value : 'TODAY'
}

const getTransactionCategoryType = (isTransfer: boolean, isSpending: boolean, isIncome: boolean): MoneyflowType | null => {
  if (isTransfer) {
    return null
  } else if (isSpending) {
    return 'spending'
  } else if (isIncome) {
    return 'income'
  }
  return null
}

export const saturateCategory = (category: CategoryId, meta: MetaData): TypedMoneyflowCategory | null => {
  const categoryId = resolveCategory(category, false)

  // TODO: `transfer` category is neither a spending nor an income
  // but back end returns it in both `spending_categories` and `income_categories`
  const spendings = meta.spending_categories.byKey[categoryId]
  const incomes = meta.income_categories.byKey[categoryId]
  const debts = meta.debt_categories.byKey[categoryId]
  const asset = meta.asset_categories.byKey[categoryId]

  const metaCategory: Category | null = spendings || incomes || debts || asset || null
  if (!metaCategory) {
    return null
  }

  const isTransfer = metaCategory && metaCategory.id === TRANSFER_CATEGORY_ID
  const categoryType = getTransactionCategoryType(isTransfer, !!spendings, !!incomes)

  return {
    ...metaCategory,
    type: categoryType,
  }
}

export const getBarLength = (amount: number, max: number) => {
  const length = (amount * 100) / max
  if (amount === 0 || length < MINIMUM_BAR_WIDTH) {
    return MINIMUM_BAR_WIDTH
  }
  return max === amount ? 100 : length
}

export function getCategoryById (id: CategoryId | null, collection: CollectionCategory): Category | null {
  return getItemFromCollection(collection, id)
}

export function getCategoryTree (collection: CollectionCategory): Category[] {
  return getItemsFromCollection(collection)
}

export function flattenCategories (groups: MoneyflowCategoryGroup[]): TypedMoneyflowCategory[] {
  return flatten(groups.map((group) => group.categories))
}

export function isCategoryType (category: TypedMoneyflowCategory, type: MoneyflowCategoryType): boolean {
  return category.type === type
}
